import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { resolveAdventureStatus, isAdventureClosedToReplies, resolveAdventureMissionType } from '../../features/adventure/shared/adventureConstants'
import { formatLastActivity } from '../TerritoryGrid/territoryUtils'
import './homePanel.css'
import './HomeActiveAdventures.css'

const RANK_LABELS = ['I', 'II', 'III']

const STATUS_UI = {
  Abierta: { label: 'Abierta', cls: 'home-active-adv__status--open' },
  'En progreso': { label: 'En curso', cls: 'home-active-adv__status--progress' },
  Completada: { label: 'Cerrada', cls: 'home-active-adv__status--completed' },
  Cancelada: { label: 'Cancelada', cls: 'home-active-adv__status--cancelled' },
}

const MISSION_ICONS = {
  Combate: '⚔',
  Espionaje: '◈',
  Rescate: '◇',
  Exploración: '◉',
  Comercio: '◆',
  Diplomacia: '✦',
  Misión: '⚔',
  'Misión Principal': '⚔',
  'Misión Secundaria': '◆',
  Entrenamiento: '☯',
  Evento: '✦',
  General: '◇',
}

function parseDate(s) {
  try {
    return s ? new Date(s) : new Date(0)
  } catch {
    return new Date(0)
  }
}

function sortByActivity(list) {
  return [...list].sort((a, b) => {
    const rb = Number(b?.replies_count || 0) - Number(a?.replies_count || 0)
    if (rb !== 0) return rb
    const pb = Number(b?.current_participants || 0) - Number(a?.current_participants || 0)
    if (pb !== 0) return pb
    return (
      parseDate(b?.last_activity || b?.updated_at || b?.created_at) -
      parseDate(a?.last_activity || a?.updated_at || a?.created_at)
    )
  })
}

export default function HomeActiveAdventures({
  adventures = [],
  loading = false,
  error = null,
  limit = 3,
}) {
  const top = useMemo(() => {
    const openOnly = (adventures || []).filter((a) => !isAdventureClosedToReplies(a))
    return sortByActivity(openOnly).slice(0, limit)
  }, [adventures, limit])

  return (
    <section
      className="home-bento-card home-bento-card--activity home-active-adventures"
      aria-label="Aventuras más activas"
    >
      <header className="home-panel__header">
        <span className="home-panel__eyebrow">戦 · Crónicas vivas</span>
        <h2 className="home-panel__title">Aventuras más activas</h2>
        <p className="home-panel__subtitle">
          Las crónicas con más respuestas y movimiento en el foro
        </p>
      </header>

      {loading && <p className="home-panel__empty">Consultando las crónicas…</p>}
      {error && !loading && <p className="home-panel__empty">{error}</p>}
      {!loading && !error && top.length === 0 && (
        <p className="home-panel__empty">Aún no hay aventuras con actividad registrada.</p>
      )}

      {!loading && !error && top.length > 0 && (
        <ol className="home-active-adventures__list">
          {top.map((adv, index) => {
            const replies = Number(adv.replies_count || 0)
            const participants = Number(adv.current_participants || 0)
            const mission = resolveAdventureMissionType(adv)
            const status = resolveAdventureStatus(adv)
            const statusUi = STATUS_UI[status] || STATUS_UI.Abierta
            const activityLabel = formatLastActivity(
              adv.last_activity || adv.updated_at || adv.created_at
            )
            const isTop = index === 0

            return (
              <li key={adv.slug || adv.adventureId || index}>
                <Link
                  to={`/aventura/${adv.slug}`}
                  className={`home-active-adv${isTop ? ' home-active-adv--top' : ''}${
                    status === 'Completada'
                      ? ' home-active-adv--closed'
                      : status === 'Cancelada'
                        ? ' home-active-adv--cancelled'
                        : ''
                  }`}
                >
                  <aside className="home-active-adv__seal" aria-hidden="true">
                    <span className="home-active-adv__rank">{RANK_LABELS[index] || String(index + 1)}</span>
                  </aside>
                  <div className="home-active-adv__main">
                    <header className="home-active-adv__kicker">
                      <span className="home-active-adv__mission">
                        <span aria-hidden="true">{MISSION_ICONS[mission] || '◇'}</span>
                        {mission}
                      </span>
                      <span className={`home-active-adv__status ${statusUi.cls}`}>{statusUi.label}</span>
                      {activityLabel && (
                        <span className="home-active-adv__time">{activityLabel}</span>
                      )}
                    </header>
                    <h3 className="home-active-adv__title">{adv.title}</h3>
                    <dl className="home-active-adv__stats">
                      <div className="home-active-adv__metric">
                        <dd className="home-active-adv__metric-value">{replies}</dd>
                        <dt className="home-active-adv__metric-label">Respuestas</dt>
                      </div>
                      <div className="home-active-adv__metric">
                        <dd className="home-active-adv__metric-value">{participants}</dd>
                        <dt className="home-active-adv__metric-label">Shinobis</dt>
                      </div>
                      {adv.territory_name && (
                        <div className="home-active-adv__metric home-active-adv__metric--wide">
                          <dd className="home-active-adv__metric-value home-active-adv__metric-value--text">
                            {adv.territory_name}
                          </dd>
                          <dt className="home-active-adv__metric-label">Territorio</dt>
                        </div>
                      )}
                    </dl>
                    <span className="home-active-adv__cta">
                      Abrir crónica
                      <span className="home-active-adv__cta-arrow" aria-hidden="true">
                        →
                      </span>
                    </span>
                  </div>
                </Link>
              </li>
            )
          })}
        </ol>
      )}

      {!loading && top.length > 0 && (
        <Link to="/aventuras" className="home-active-adventures__more">
          Ver tablón de aventuras →
        </Link>
      )}
    </section>
  )
}
